import * as XLSX from "xlsx";

// Columns we care about, in this exact order.
// DDPO and SLPO are computed live and intentionally NOT stored columns.
export const COLS = ["RANK", "S", "DD", "ML", "S2", "SLLWP", "SLML"];

// Two independent halves of the race card:
//   LEFT  = first  leg of the Daily Double : { RANK, S, DD, ML }
//   RIGHT = second leg of the Daily Double : { S2, SLLWP, SLML }

export const buildBlankLeftRows = (n = 12) =>
  Array.from({ length: n }, (_, i) => ({
    RANK: "",
    S: i + 1,
    DD: "",
    ML: "",
  }));

export const buildBlankRightRows = (n = 12) =>
  Array.from({ length: n }, (_, i) => ({
    S2: i + 1,
    SLLWP: "",
    SLML: "",
  }));

// Parse a number-like value. Supports 3, "3", "3.5", "1,000", "5/2" (fraction),
// and returns null for #DIV/0!, empty, non-numeric.
export const parseNumeric = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return isFinite(val) ? val : null;
  const s = String(val).trim();
  if (!s) return null;
  if (s.startsWith("#")) return null;
  if (s.includes("/")) {
    const [a, b] = s.split("/").map((p) => Number(p.trim()));
    if (isFinite(a) && isFinite(b) && b !== 0) return a / b;
    return null;
  }
  const n = Number(s.replace(/[, ]/g, ""));
  return isFinite(n) ? n : null;
};

export const NO_RATIO = "—";

export const computeDDPO = (dd, ml) => {
  const a = parseNumeric(dd);
  const b = parseNumeric(ml);
  if (a === null || b === null || b === 0) return NO_RATIO;
  return Number((a / b).toFixed(4)).toString();
};

export const computeSLPO = (sllwp, slml) => {
  const a = parseNumeric(sllwp);
  const b = parseNumeric(slml);
  if (a === null || b === null || b === 0) return NO_RATIO;
  return Number((a / b).toFixed(4)).toString();
};

// Shared stable ascending-sort with invalids pushed to the bottom.
const sortAscInvalidsLast = (indexed, saddleKey) => {
  indexed.sort((a, b) => {
    const aBad = a.v === null;
    const bBad = b.v === null;
    if (aBad && bBad) {
      const aS = Number(a.r[saddleKey]) || a.i;
      const bS = Number(b.r[saddleKey]) || b.i;
      return aS - bS;
    }
    if (aBad) return 1;
    if (bBad) return -1;
    if (a.v !== b.v) return a.v - b.v;
    return a.i - b.i;
  });
};

// ----- IQ Power -------------------------------------------------------------
// IQ Power = 50 + ML points + Live Odds points + DDPO points. Higher = better.
//   ML / Live: lowest odds 12.2, then 11.2, 10.2 … (ties share, never below 0).
//   DDPO: 49.2 split among the 4 lowest DDPOs in proportion to 1/DDPO.
// Cybertote's 1,000 cap is a placeholder, not a price → "No Rank".
export const NO_RANK_CAP = 1000;
export const IQ_BASE = 50;
export const TOP_ODDS_POINTS = 12.2;
export const DDPO_POOL = 49.2;
export const DDPO_SLOTS = 4;

// "5-2", "5/2", "3", "3.5" → decimal odds; "SC", "--", "" → null.
export const parseOdds = (v) => {
  const s = String(v ?? "").trim();
  const m = s.match(/^(\d+(?:\.\d+)?)\s*[-/]\s*(\d+(?:\.\d+)?)$/);
  if (m) return Number(m[2]) ? Number(m[1]) / Number(m[2]) : null;
  return /^\d+(?:\.\d+)?$/.test(s) ? Number(s) : null;
};

const isScratch = (v) => /^SC/i.test(String(v ?? "").trim());

// Competition ranking: ties share a rank, next skips (1, 2, 3, 3, 5).
export const competitionRanks = (items) => {
  const valid = items.filter((it) => it.odds != null);
  const out = new Map();
  for (const it of valid) {
    out.set(it.key, 1 + valid.filter((o) => o.odds < it.odds).length);
  }
  return out;
};

export const rankPoints = (rank) =>
  rank == null ? 0 : Math.max(0, TOP_ODDS_POINTS - (rank - 1));

// {key, ratio}[] → Map key → share of 49.2 for the 4 lowest (ties at 4th included).
export const ddpoPoints = (items) => {
  const ranks = competitionRanks(items.map((it) => ({ key: it.key, odds: it.ratio })));
  const top = items.filter((it) => it.ratio > 0 && ranks.get(it.key) <= DDPO_SLOTS);
  const inv = top.reduce((a, it) => a + 1 / it.ratio, 0);
  return new Map(top.map((it) => [it.key, (DDPO_POOL * (1 / it.ratio)) / inv]));
};

const metricsFor = (rows, { post, will, line, mlOf, oddsOf }) => {
  const base = rows.map((r) => {
    const w = parseNumeric(r[will]);
    const l = parseNumeric(r[line]);
    const noRank = w !== null && w >= NO_RANK_CAP;
    const ratio = w === null || l === null || l === 0 ? null : w / l;
    const odds = oddsOf(r);
    const ml = mlOf(r);
    // Missing DD / SLLWP → still ranks, just earns 0 DDPO points.
    const valid = !noRank && odds !== null && ml !== null;
    return { key: Number(r[post]), ratio, odds, ml, noRank, valid, bonus: Number(r.BONUS) || 0 };
  });
  const pool = base.filter((b) => b.valid);
  const oddsRanks = competitionRanks(pool.map((b) => ({ key: b.key, odds: b.odds })));
  const mlRanks = competitionRanks(pool.map((b) => ({ key: b.key, odds: b.ml })));
  const ddpo = ddpoPoints(pool);
  return base.map(({ key, ratio, odds, noRank, valid, bonus }) => {
    const oddsRank = valid ? oddsRanks.get(key) : null;
    const mlRank = valid ? mlRanks.get(key) : null;
    const ddpoPts = valid ? ddpo.get(key) ?? 0 : null;
    const iq = valid
      ? IQ_BASE + rankPoints(mlRank) + rankPoints(oddsRank) + ddpoPts + bonus
      : null;
    return { ratio, oddsRank, mlRank, ddpoPts, odds, iq, noRank, bonus };
  });
};

// Leg 1: live odds (row.LIVE), Morning Line until the Win pool reports.
export const leftMetrics = (rows) =>
  metricsFor(rows, {
    post: "S",
    will: "DD",
    line: "ML",
    mlOf: (r) => (isScratch(r.LIVE) ? null : parseOdds(r.ML)),
    oddsOf: (r) => (isScratch(r.LIVE) ? null : parseOdds(r.LIVE) ?? parseOdds(r.ML)),
  });

// Leg 2: SLPO in place of DDPO; no live odds before leg 1 goes off → ML for both.
export const rightMetrics = (rows) =>
  metricsFor(rows, {
    post: "S2",
    will: "SLLWP",
    line: "SLML",
    mlOf: (r) => parseOdds(r.SLML),
    oddsOf: (r) => parseOdds(r.SLML),
  });

// Post with the lowest DDPO among rankable, non-eliminated leg-1 horses.
export const lowestDdpoPost = (rows, elimSet = null) => {
  const m = leftMetrics(rows);
  let best = null;
  rows.forEach((r, i) => {
    if (m[i].iq === null || m[i].ratio === null || (elimSet instanceof Set && elimSet.has(Number(r.S)))) return;
    if (best === null || m[i].ratio < best.ratio) best = { post: Number(r.S), ratio: m[i].ratio };
  });
  return best ? best.post : null;
};

export const fmtIq = (v) => (v == null ? NO_RATIO : v.toFixed(1));

const sortByIq = (rows, metrics, saddleKey) => {
  // Higher IQ Power ranks first: negate for the ascending sorter.
  const indexed = rows.map((r, i) => ({ r, i, v: metrics[i].iq === null ? null : -metrics[i].iq }));
  sortAscInvalidsLast(indexed, saddleKey);
  return indexed;
};

// Handicap LEFT half: sort by IQ Power desc, refill RANK 1..N (blank for invalids).
export const handicapLeft = (leftRows) =>
  sortByIq(leftRows, leftMetrics(leftRows), "S").map(({ r, v }, idx) => ({
    ...r,
    RANK: v === null ? "" : idx + 1,
  }));

// Handicap RIGHT half: sort by IQ Power desc; row position IS the rank.
export const handicapRight = (rightRows) =>
  sortByIq(rightRows, rightMetrics(rightRows), "S2").map(({ r }) => r);

// Rank maps for movement arrows: post → rank (1 = best).
export const leftRankMap = (rows) =>
  new Map(rows.filter((r) => r.RANK).map((r) => [Number(r.S), Number(r.RANK)]));

export const rightRankMap = (rows) => {
  const m = rightMetrics(rows);
  return new Map(
    rows.filter((_, i) => m[i].iq !== null).map((r, idx) => [Number(r.S2), idx + 1])
  );
};

// {post: places moved} — positive = moved up (better), negative = dropped.
export const diffRanks = (prev, next) => {
  const out = {};
  for (const [post, rank] of next) {
    const before = prev.get(post);
    if (before != null && before !== rank) out[post] = before - rank;
  }
  return out;
};

export const hasValidLeft = (leftRows) => leftMetrics(leftRows).some((m) => m.iq !== null);

export const hasValidRight = (rightRows) => rightMetrics(rightRows).some((m) => m.iq !== null);

// ----- Excel helpers kept for potential future use ---------------------------
const norm = (k) => String(k || "").trim().toLowerCase();
const HEADER_MAP = {
  rank: "RANK",
  s: "S",
  saddle: "S",
  post: "S",
  pp: "S",
  dd: "DD",
  ml: "ML",
  "morning line": "ML",
  s2: "S2",
  sllwp: "SLLWP",
  slml: "SLML",
};

const mapRow = (raw) => {
  const out = {
    RANK: "",
    S: "",
    DD: "",
    ML: "",
    S2: "",
    SLLWP: "",
    SLML: "",
  };
  for (const k of Object.keys(raw)) {
    const target = HEADER_MAP[norm(k)];
    if (target) out[target] = raw[k];
  }
  return out;
};

export const readWorkbookFile = async (file) => {
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
  return json.map(mapRow);
};
